import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import './App.css';

// Smooth scroll function for anchor links
const useScrollToSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };
  
  return scrollToSection;
};

// Layout component with navigation
function Layout({ children }) {
  const [theme, setTheme] = useState(() => {
    // Initialize from localStorage or default to light
    if (typeof window !== 'undefined') {
      return localStorage.getItem('redactweb-theme') || 'light';
    }
    return 'light';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const scrollToSection = useScrollToSection();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('redactweb-theme', theme);
  }, [theme]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return (
    <div className="app" data-testid="app-container">
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <div className="logo-icon">RW</div>
            <span className="logo-text">RedactWeb</span>
          </Link>

          <div className="nav-links desktop-only">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')}>Features</a>
            <a href="#faq" onClick={(e) => scrollToSection(e, 'faq')}>FAQ</a>
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')}>Pricing</a>
          </div>

          <div className="nav-actions">
            <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="btn btn-primary desktop-only">Get License</a>
            <button className="mobile-menu-btn mobile-only" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <MenuIcon />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-menu">
            <a href="#features" onClick={(e) => { scrollToSection(e, 'features'); setMobileMenuOpen(false); }}>Features</a>
            <a href="#faq" onClick={(e) => { scrollToSection(e, 'faq'); setMobileMenuOpen(false); }}>FAQ</a>
            <a href="#pricing" onClick={(e) => { scrollToSection(e, 'pricing'); setMobileMenuOpen(false); }}>Pricing</a>
          </div>
        )}
      </nav>

      {children}

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Link to="/" className="nav-logo">
                <div className="logo-icon">RW</div>
                <span className="logo-text">RedactWeb</span>
              </Link>
              <p>Blur and protect sensitive content on any website.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#features" onClick={(e) => scrollToSection(e, 'features')}>Features</a>
                <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')}>Pricing</a>
                <a href="#faq" onClick={(e) => scrollToSection(e, 'faq')}>FAQ</a>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <a href="mailto:support@redactweb.com">Contact</a>
                <a href="#faq" onClick={(e) => scrollToSection(e, 'faq')}>FAQ</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 RedactWeb by Tatomir N. Alex PFA. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Home Page
function HomePage() {
  const scrollToSection = useScrollToSection();
  
  // ============================================
  // VIDEO URL - MODIFY THIS LINE TO CHANGE VIDEO
  // ============================================
  const DEMO_VIDEO_URL = "redactweb_intro.mp4";
  // ============================================
  
  const features = [
    {
      icon: <BlurIcon />,
      title: 'Smart Element Blur',
      description: 'One click to blur any element on the page. Passwords, emails, bank balances - gone in an instant.'
    },
    {
      icon: <RectIcon />,
      title: 'Custom Blur Zones',
      description: 'Draw rectangles over any area you want to hide. Perfect for charts, tables, or multi-element sections.'
    },
    {
      icon: <HighlightIcon />,
      title: 'Highlight Element',
      description: 'Mark important content with bright highlights. Make your point clear during demos and walkthroughs.'
    },
    {
      icon: <ProtectIcon />,
      title: 'Blackout Element',
      description: 'Cover elements with a solid black overlay. Completely hide sensitive content from view.'
    },
    {
      icon: <ScreenshotIcon />,
      title: 'Quick Screenshot',
      description: 'Capture your redacted page instantly in PNG or JPG format. Perfect for sharing or documentation.'
    },
    {
      icon: <EyeOffIcon />,
      title: 'Stealth Tab Mode',
      description: 'Hide your tab title and favicon instantly. Nobody will know what site you have open.'
    },
    {
      icon: <SaveIcon />,
      title: 'Persistent Memory',
      description: 'RedactWeb remembers your blur settings for each page. Return anytime and your redactions are waiting.'
    },
    {
      icon: <KeyboardIcon />,
      title: 'Speed Shortcuts',
      description: 'Blur with Alt+B, draw zones with Alt+Z, highlight with Alt+H. Work faster with keyboard controls.'
    },
    {
      icon: <ClearIcon />,
      title: 'One-Click Clear All',
      description: 'Remove all redactions instantly with a single click. Start fresh whenever you need to.'
    }
  ];

  const faqs = [
    {
      question: 'Does RedactWeb access or store my personal data?',
      answer: 'No. RedactWeb operates 100% locally in your browser. We have no servers collecting your data. Your browsing activity, blurred content, and personal information never leave your device.'
    },
    {
      question: 'Which browsers are supported?',
      answer: 'RedactWeb works with Chrome, Edge, Brave, Opera, Vivaldi, Arc, and any other Chromium-based browser. Firefox support is planned for the future.'
    },
    {
      question: 'How does the license activation work?',
      answer: 'After purchasing, you receive a license key via email. Open the RedactWeb popup, enter your key, and click Activate. The process takes about 5 seconds.'
    },
    {
      question: 'Do I need technical knowledge to use it?',
      answer: 'Not at all. If you can click a mouse, you can use RedactWeb. The interface is designed to be intuitive - just click what you want to blur.'
    },
    {
      question: 'Does the extension affect browser performance?',
      answer: 'RedactWeb uses minimal resources and only activates when you need it. You won\'t notice any slowdown in your daily browsing.'
    },
    {
      question: 'What if I\'m not satisfied with the product?',
      answer: 'We offer a full refund within 30 days of purchase, no questions asked. Just send us an email and we\'ll process your refund immediately.'
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="container">
          <div className="hero-badge">
            <ShieldIcon />
            <span>Screen Privacy Tool</span>
          </div>
          <h1 className="hero-title">
            Hide Sensitive Information
            <span className="gradient-text"> Before Anyone Sees It</span>
          </h1>
          <p className="hero-subtitle">
            A browser extension that lets you blur, hide, and protect private data on any webpage in real-time. Ideal for demos, screenshots, and screen recordings.
          </p>
          <div className="hero-actions">
            <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="btn btn-primary btn-lg">
              Get RedactWeb
            </a>
            <a href="#demo" onClick={(e) => scrollToSection(e, 'demo')} className="btn btn-secondary btn-lg">
              <PlayIcon />
              See How It Works
            </a>
          </div>
          <div className="hero-trust">
            <span>Free 7-day trial</span>
            <span className="dot">•</span>
            <span>No account required</span>
            <span className="dot">•</span>
            <span>30-day refund guarantee</span>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="demo" id="demo">
        <div className="container">
          <div className="video-wrapper">
            <div className="video-container">
              <video 
                className="demo-video"
                controls
                playsInline
                preload="metadata"
                poster="redactweb_poster.jpg"
                data-testid="demo-video"
              >
                <source src={DEMO_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Features</span>
            <h2 className="section-title">Built for people who share their screen</h2>
            <p className="section-subtitle">
              Whether you're recording tutorials, presenting to clients, or taking screenshots - RedactWeb keeps your private data private.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">How It Works</span>
            <h2 className="section-title">Three steps to complete privacy</h2>
            <p className="section-subtitle">
              No configuration needed. Install and start protecting your screen immediately.
            </p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Add to Browser</h3>
              <p>Install from Chrome Web Store or download directly</p>
            </div>
            <div className="step-arrow"><ArrowRightIcon /></div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Activate Toolbar</h3>
              <p>Click the RW icon in your browser toolbar</p>
            </div>
            <div className="step-arrow"><ArrowRightIcon /></div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Start Redacting</h3>
              <p>Click elements to blur or draw zones to hide areas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Single Card */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Pricing</span>
            <h2 className="section-title">One Price, Forever Yours</h2>
            <p className="section-subtitle">Pay once and use RedactWeb for life. No subscriptions, no hidden fees.</p>
          </div>
          <div className="pricing-single">
            <div className="pricing-card pricing-card-featured">
              <div className="pricing-badge">Lifetime Access</div>
              <div className="pricing-header">
                <div className="pricing-price">
                  <span className="price-amount">$39</span>
                  <span className="price-period">one-time payment</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li><CheckIcon /> 7-day free trial included</li>
                <li><CheckIcon /> Blur any element with one click</li>
                <li><CheckIcon /> Draw custom blur zones</li>
                <li><CheckIcon /> Highlight elements</li>
                <li><CheckIcon /> Blackout elements completely</li>
                <li><CheckIcon /> Screenshot with one click</li>
                <li><CheckIcon /> Hide tab title and favicon</li>
                <li><CheckIcon /> Keyboard shortcuts for speed</li>
                <li><CheckIcon /> Settings saved per website</li>
                <li><CheckIcon /> Works on all Chromium browsers</li>
                <li><CheckIcon /> All future updates included</li>
              </ul>
              <a href="mailto:support@redactweb.com?subject=RedactWeb%20License%20Purchase" className="btn btn-primary btn-block btn-lg" data-testid="buy-license-btn">
                Buy Now - $39
              </a>
              <p className="pricing-guarantee">Full refund within 30 days if you're not happy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="download" id="download">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Download</span>
            <h2 className="section-title">Get Started with Free Trial</h2>
            <p className="section-subtitle">Download RedactWeb for your browser. Try free for 7 days, no account needed.</p>
          </div>
          <div className="download-buttons">
            <a href="#" className="download-btn" data-testid="download-chrome">
              <ChromeIcon />
              <div className="download-btn-text">
                <span className="download-label">Download for</span>
                <span className="download-browser">Google Chrome</span>
              </div>
            </a>
            <a href="#" className="download-btn" data-testid="download-edge">
              <EdgeIcon />
              <div className="download-btn-text">
                <span className="download-label">Download for</span>
                <span className="download-browser">Microsoft Edge</span>
              </div>
            </a>
          </div>
          <p className="download-note">Works on all Chromium-based browsers (Chrome, Edge, Brave, Opera, Arc)</p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">FAQ</span>
            <h2 className="section-title">Common Questions</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                className={`faq-item ${openFaq === index ? 'open' : ''}`} 
                key={index}
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
              >
                <div className="faq-question">
                  <span>{faq.question}</span>
                  <ChevronIcon />
                </div>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Ready to protect your screen?</h2>
          <p>Start your free trial today. No credit card needed.</p>
          <a href="#pricing" onClick={(e) => scrollToSection(e, 'pricing')} className="btn btn-primary btn-lg">
            Get RedactWeb Now
          </a>
          <div className="cta-trust">
            <span>Instant license delivery</span>
            <span className="dot">•</span>
            <span>30-day refund policy</span>
          </div>
        </div>
      </section>
    </>
  );
}

// Privacy Policy Page
function PrivacyPage() {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="legal-date">Last updated: January 2026</p>
        
        <section>
          <h2>Introduction</h2>
          <p>RedactWeb ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we handle information when you use our browser extension and website.</p>
        </section>

        <section>
          <h2>Information We Don't Collect</h2>
          <p>Our browser extension operates entirely locally on your device. We do not:</p>
          <ul>
            <li>Collect, store, or transmit any data you blur or interact with</li>
            <li>Track your browsing history or activity</li>
            <li>Send any personal information to our servers</li>
            <li>Use cookies for tracking purposes</li>
          </ul>
        </section>

        <section>
          <h2>Information We Collect</h2>
          <p>We only collect the minimum information necessary to provide our services:</p>
          <ul>
            <li><strong>License Information:</strong> When you purchase a license, we store your email address and license key to validate your purchase.</li>
            <li><strong>Payment Information:</strong> Payment processing is handled by third-party providers (e.g., Stripe). We do not store your credit card details.</li>
          </ul>
        </section>

        <section>
          <h2>Local Storage</h2>
          <p>The extension uses your browser's local storage to save:</p>
          <ul>
            <li>Your blur preferences per website</li>
            <li>Toolbar position settings</li>
            <li>Theme preferences</li>
            <li>License validation status</li>
          </ul>
          <p>This data never leaves your device.</p>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <p>We may use third-party services for:</p>
          <ul>
            <li>Payment processing (Stripe)</li>
            <li>Email delivery for license keys</li>
          </ul>
          <p>These services have their own privacy policies.</p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>If you have questions about this Privacy Policy, please contact us at:</p>
          <p><a href="mailto:support@redactweb.com">support@redactweb.com</a></p>
        </section>
      </div>
    </div>
  );
}

// Terms of Service Page
function TermsPage() {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Terms of Service</h1>
        <p className="legal-date">Last updated: January 2026</p>
        
        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By installing or using RedactWeb ("the Extension"), you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the Extension.</p>
        </section>

        <section>
          <h2>2. License Grant</h2>
          <p>Upon purchase of a valid license, we grant you a non-exclusive, non-transferable, limited license to use the Extension for personal or business purposes.</p>
          <ul>
            <li>The license is tied to your email address</li>
            <li>You may use the license on unlimited browsers/devices for personal use</li>
            <li>You may not share, sell, or distribute your license key</li>
          </ul>
        </section>

        <section>
          <h2>3. Free Trial</h2>
          <p>We offer a 7-day free trial with full functionality. After the trial period:</p>
          <ul>
            <li>You must purchase a license to continue using the Extension</li>
            <li>Trial access cannot be renewed or extended</li>
          </ul>
        </section>

        <section>
          <h2>4. Refund Policy</h2>
          <p>We offer a 30-day money-back guarantee. If you are not satisfied with the Extension, contact us within 30 days of purchase for a full refund.</p>
        </section>

        <section>
          <h2>5. Prohibited Uses</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Reverse engineer, decompile, or disassemble the Extension</li>
            <li>Use the Extension for any illegal purposes</li>
            <li>Share or distribute your license key</li>
            <li>Attempt to bypass the license validation system</li>
          </ul>
        </section>

        <section>
          <h2>6. Disclaimer of Warranties</h2>
          <p>The Extension is provided "as is" without warranties of any kind. We do not guarantee that the Extension will work on all websites or in all circumstances.</p>
        </section>

        <section>
          <h2>7. Limitation of Liability</h2>
          <p>In no event shall RedactWeb or its creator be liable for any indirect, incidental, special, or consequential damages arising from your use of the Extension.</p>
        </section>

        <section>
          <h2>8. Changes to Terms</h2>
          <p>We reserve the right to modify these terms at any time. Continued use of the Extension after changes constitutes acceptance of the new terms.</p>
        </section>

        <section>
          <h2>9. Contact</h2>
          <p>For questions about these Terms, contact us at:</p>
          <p><a href="mailto:support@redactweb.com">support@redactweb.com</a></p>
        </section>
      </div>
    </div>
  );
}

// Main App Component with Router
function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
        <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
      </Routes>
    </HashRouter>
  );
}

// Icons
const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const BlurIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="6" opacity="0.5"/>
    <circle cx="12" cy="12" r="3" opacity="0.3"/>
  </svg>
);

const RectIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2"/>
  </svg>
);

const HighlightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="18" x2="12" y2="21"/>
    <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5"/>
  </svg>
);

const ProtectIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <line x1="3" y1="3" x2="21" y2="21"/>
    <line x1="21" y1="3" x2="3" y2="21"/>
  </svg>
);

const ScreenshotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <circle cx="12" cy="13" r="3"/>
    <path d="M5 3v2M19 3v2M12 3v2"/>
  </svg>
);

const ClearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const SaveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

const KeyboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <line x1="6" y1="8" x2="6" y2="8"/>
    <line x1="10" y1="8" x2="10" y2="8"/>
    <line x1="14" y1="8" x2="14" y2="8"/>
    <line x1="18" y1="8" x2="18" y2="8"/>
    <line x1="6" y1="12" x2="6" y2="12"/>
    <line x1="18" y1="12" x2="18" y2="12"/>
    <line x1="8" y1="16" x2="16" y2="16"/>
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const ChromeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
    <circle cx="12" cy="12" r="4" fill="currentColor"/>
    <path d="M21.17 8H12" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M7.4 19.4L12 12" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M7.4 4.6L12 12" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const EdgeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M21 12c0-4.97-4.03-9-9-9-2.83 0-5.35 1.31-7 3.35 1.65-1.31 3.74-2.1 6-2.1 5.25 0 9.5 4.25 9.5 9.5 0 1.38-.3 2.69-.83 3.87.52-1.18.83-2.49.83-3.87 0-.25-.01-.5-.03-.75H12c0 2.21-1.79 4-4 4-.74 0-1.43-.2-2.03-.55C7.17 18.5 9.43 20 12 20c4.97 0 9-4.03 9-9z"/>
    <circle cx="8" cy="14" r="3"/>
  </svg>
);

export default App;
